import { TipPageLoad } from '@analytics/store';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { CompleteOrderService } from '@complete-order/services/complete-order.service';
import { BaseComponent } from '@core/components/base/base.component';
import {
  fadeIn,
  fadeInOut,
  Offers,
  OfferStatus,
} from '@dgx-pr-spa/shared/util';
import { NavigationLink } from '@domgen/dgx-fe-common';
import { Picture } from '@domgen/dgx-fe-components/media';
import { Store } from '@ngrx/store';
import { LiveChatService } from '@pages/services/live-chat.service';
import { UrlService } from '@shared/services/url.service';
import { ViewOffersService } from '@view-offers/services/view-offers.service';
import posthog from 'posthog-js';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-pr-root',
  templateUrl: './pr-app.component.html',
  styleUrls: ['./pr-app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [fadeIn, fadeInOut],
})
export class PrAppComponent
  extends BaseComponent
  implements OnInit, AfterViewInit
{
  @ViewChild('headerNavWrapper', { static: false })
  headerNavWrapper: ElementRef;

  offersState$ = this.viewOffersService.offersState$;
  offers$ = this.viewOffersService.offers$;
  ref = '';

  previousUrl: string = null;
  currentUrl: string = null;

  navigationLinks: NavigationLink[] = [
    {
      href: 'https://www.domesticandgeneral.com/content/contact-domestic-general',
      label: 'Contact Us',
      newWindow: true,
    },
    {
      href: 'https://www.domesticandgeneral.com/content/help-advice-section/faq',
      label: 'FAQ',
      newWindow: true,
    },
  ];

  clientLogo: Picture = {
    sourceSet: {},
    src: './assets/logo/domestic-and-general.svg',
    aspectRatio: '16:9',
    alt: 'Domestic & General Logo',
  };

  constructor(
    readonly router: Router,
    private titleService: Title,
    public readonly route: ActivatedRoute,
    public readonly viewOffersService: ViewOffersService,
    public readonly completeOrderService: CompleteOrderService,
    private readonly liveChatService: LiveChatService,
    private urlService: UrlService,
    private store: Store,
    private renderer: Renderer2
  ) {
    super();
  }

  prepareRoute(outlet: RouterOutlet) {
    return (
      outlet && outlet.activatedRouteData && outlet.activatedRouteData.animation
    );
  }

  /**
   * set individual page title based on data object configured in app-routing.module.ts
   */
  ngOnInit() {
    this.liveChatService.initialiseMessenger();
    posthog.identify(this.route?.snapshot?.queryParams?.['PRRef']);
    const defaultTitle = 'Domestic & General';
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event: NavigationEnd) => {
          const child = this.route.firstChild;
          this.previousUrl = this.currentUrl;
          this.currentUrl = event.url;
          this.urlService.setPreviousUrl(this.previousUrl);

          if (child?.snapshot?.data.title) {
            return `${child.snapshot.data.title} | ${defaultTitle}`;
          }

          // fallback
          return defaultTitle;
        }),
        switchMap((title) => {
          this.titleService.setTitle(title);
          return this.offersState$;
        }),
        filter(({ loaded }) => !loaded),
        switchMap(() => this.initialiseOffers())
      )
      .subscribe((state) => {
        if (!this.router.url.includes('order-confirmation')) {
          this.handleRedirect(state.data);
        }
      });
  }

  ngAfterViewInit(): void {
    this.amendLogoElement();
    this.fixHeaderKeyboardNavigation();
  }

  private fixHeaderKeyboardNavigation(): void {
    const headerEl =
      this.headerNavWrapper?.nativeElement?.querySelector('.header');

    if (!headerEl) {
      return;
    }

    const homeLink = headerEl.querySelector(
      '.header__home-link'
    ) as HTMLElement;
    const focusableElements = Array.from(
      headerEl.querySelectorAll(
        'a[href], button, [tabindex]:not([tabindex="-1"])'
      )
    ) as HTMLElement[];

    focusableElements.forEach((el: HTMLElement) => {
      const tabindex = el.getAttribute('tabindex');

      if (tabindex && parseInt(tabindex, 10) > 0) {
        el.removeAttribute('tabindex');
      }
    });

    if (homeLink) {
      homeLink.setAttribute('tabindex', '1');
    }
  }

  private amendLogoElement(): void {
    const logoEl = this.headerNavWrapper.nativeElement.querySelector(
      '.header__home-link-picture'
    );

    if (logoEl) {
      this.renderer.addClass(logoEl, 'disabled-click');

      this.renderer.listen(logoEl, 'click', (event: Event) => {
        event.preventDefault();
      });
    }
  }

  initialiseOffers() {
    return this.route.queryParams.pipe(
      takeUntil(this.destroy$),
      map(({ PRRef }) => (this.ref = PRRef ? PRRef.replace(/\//g, '') : '')),
      tap(() => {
        this.viewOffersService.loadOffers(this.ref);
      }),
      switchMap(() => this.offersState$),
      filter(({ loaded }) => loaded),
      tap(({ data }: { data: Offers }) => {
        if (
          data?.offer?.offerReference &&
          data.offer.offerReference !== this.ref
        ) {
          this.ref = data.offer.offerReference;
          this.viewOffersService.setOfferReference(data.offer.offerReference);
        }

        this.completeOrderService.resetDirectDebitConfig();
      }),
      take(1)
    );
  }

  handleRedirect(offers: Offers = {} as Offers) {
    const offerStatus = offers.offer?.offer_status;

    if (offers.isProcessingOrder) {
      this.router.navigate(['processing-order'], {
        queryParams: { PRRef: this.ref },
        replaceUrl: true,
      });
      return;
    }

    if (!offerStatus) {
      this.router.navigate(['error'], {
        replaceUrl: true,
      });
    } else if (offerStatus === OfferStatus.TAKEN) {
      this.router.navigate(['offer-taken'], {
        queryParams: { PRRef: this.ref },
        replaceUrl: true,
      });
    } else {
      this.router.navigate(['tips'], {
        queryParams: { PRRef: this.ref },
        queryParamsHandling: 'merge',
      });

      this.store.dispatch(TipPageLoad());
    }
  }
}
